/**
 * Search Controller V2 - CSR (Client Side Rendering) based Search Results Page
 *
 * Flow:
 * 1. V2 API call -> Get full product data + filterOptions
 * 2. Client-side HTML rendering (no Shopify Search API)
 * 3. Update DOM
 */
(function() {
  'use strict';

  class SearchController {
    static API_BASE_URL = FILA_API_BASE_URL + '/api/v3/products/search';
    static CACHE_TTL = 0; // cache disabled: search must reflect Shopify changes immediately
    static DEFAULT_PAGE_SIZE = 20;
    static SRCSET_WIDTHS = [165, 360, 533, 720, 940, 1066, 1500];
    static GENDER_DISPLAY_NAMES = { M: '남성', F: '여성', X: '공용', U: '공용' };

    static FILTER_DEBOUNCE_DELAY = 300;

    // Category tab -> API parameter mapping
    static CATEGORY_MAP = {
      'all':  { section: null },
      '여성': { section: 'women' },
      '남성': { section: 'men' },
      '키즈': { section: 'kids' }
    };

    static SIZE_GROUP_MAP = {
      'XS': ['75(XS)', '85(XS)', '075', 'XS(075)', 'XS(085)'],
      'S': ['80(S)', '90(S)', 'S(080)', 'S(090)', '080'],
      'M': ['85(M)', '95(M)', 'M(085)', 'M(095)', '085'],
      'L': ['100(L)', '90(L)', 'L(100)', 'L(90)', '090'],
      'XL': ['105(XL)', '95(XL)', '095', 'XL(105)', 'XL(95)'],
      '2XL': ['100(2XL)', '110(2XL)', '2XL(100)', '2XL(110)', '100'],
      '3XL': ['3XL(105)', '3XL(115)', '105'],
      '4XL': ['4XL(110)', '4XL(120)', '110'],
      'WXS': ['W70(WXS)', 'W80(WXS)', 'WXS(W70)', 'WXS(W80)'],
      'WS': ['W75(WS)', 'W75', 'W85(WS)', 'WS(W95)', 'WS(W75)', 'WS(W85)'],
      'WM': ['W80', 'W80(WM)', 'W90(WM)', 'WM(W80)', 'WM(W90)'],
      'WL': ['W85', 'W85(WL)', 'W95(WL)', 'WL(W85)', 'WL(W95)'],
      'WXL': ['W87(WXL)', 'W97(WXL)', 'W87', 'W97']
    };

    constructor() {
      this.state = {
        keyword: '',
        category: 'all',  // URL filter.p.tag value
        sortBy: 'new',
        page: 1,
        pageSize: SearchController.DEFAULT_PAGE_SIZE,
        totalCount: 0,
        totalPages: 1,
        // Filter state
        colors: [],
        fromPrice: null,
        toPrice: null,
        genders: [],
        sizes: [],
        sports: [],
        wireTypes: [],
        sustainable: false,
        filterTemplate: null
      };

      this.productGrid = null;
      this.isLoading = false;
      this.tagsList = [];
      this.filterDebounceTimer = null;
      this.colorCountsCache = null;
      this.reduceIconByHandle = {};

      this.init();
    }

    debouncedFetchAndRender() {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = setTimeout(() => {
        this.fetchAndRender();
      }, SearchController.FILTER_DEBOUNCE_DELAY);
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    async setup() {
      this.productGrid = document.getElementById('product-grid');
      if (!this.productGrid) {
        console.warn('SearchController: Product grid not found');
        return;
      }

      // Read keyword and tagsList from data attributes (set by Liquid)
      this.state.keyword = this.productGrid.dataset.searchKeyword || '';
      this.tagsList = (this.productGrid.dataset.tagsList || '공용,여성,남성,키즈,남아,여아')
        .split(',')
        .map(function (t) { return t.trim(); })
        .filter(function (t) { return t.length > 0; });

      // Restore state from URL
      this.restoreStateFromURL();

      // Setup event listeners
      this.setupEventListeners();

      // Expose instance globally (for main-search.js integration)
      window.searchController = this;

      // Initial load - only if keyword exists
      if (this.state.keyword) {
        await this.fetchAndRender();
      } else {
        this.showEmptyState('검색어를 입력해주세요.');
      }
    }

    // ==========================================
    // Main rendering flow
    // ==========================================

    async fetchAndRender() {
      if (this.isLoading) return;

      try {
        this.isLoading = true;
        this.showLoadingState();

        // 1. Call V2 API to get full product data
        const apiData = await this.fetchSearchResults();

        // 2. Update UI metadata
        this.state.totalCount = apiData.totalCount || 0;
        this.state.totalPages = apiData.totalPages || 1;
        this.updateProductCount(apiData.totalCount);

        // 3. Update color filter counts
        if (apiData.colorCounts) {
          this.colorCountsCache = apiData.colorCounts;
          this.updateColorCounts(apiData.colorCounts);
        }

        // 3-1. Update filter options
        if (apiData.filterOptions) {
          this.updateFilterOptions(apiData.filterOptions);
        }

        // 3-2. Update filter template
        if (apiData.filterTemplate) {
          this.state.filterTemplate = apiData.filterTemplate;
        }

        // 4. Render products via CSR
        if (apiData.products && apiData.products.length > 0) {
          this.renderProductCards(apiData.products);
        } else {
          this.showEmptyState();
        }

        // 5. Update pagination
        this.updatePagination();

        // 6. Dispatch event for other components (swatch/inventory)
        this.dispatchCollectionReloaded();

        // 7. Restore filter UI states
        this.restorePriceFilterUI();
        this.restoreSizeFilterUI();
        this.renderActiveFacets();

      } catch (error) {
        console.error('SearchController: Fetch failed', error);
        this.showErrorState('상품을 불러오는데 실패했습니다. 페이지를 새로고침해주세요.');
      } finally {
        this.isLoading = false;
        this.hideLoadingState();
      }
    }

    // ==========================================
    // API
    // ==========================================

    async fetchSearchResults() {
      const cacheKey = this.buildCacheKey();
      const cached = this.getFromCache(cacheKey);

      if (cached) {
        console.log('SearchController: Using cached data');
        return cached;
      }

      const params = new URLSearchParams({
        keyword: this.state.keyword,
        sortBy: this.state.sortBy,
        page: this.state.page.toString(),
        pageSize: this.state.pageSize.toString()
      });

      // Map category to section parameter
      const categoryConfig = SearchController.CATEGORY_MAP[this.state.category] || SearchController.CATEGORY_MAP['all'];
      if (categoryConfig.section) {
        params.set('section', categoryConfig.section);
      }

      // User filter genders (independent of category tab)
      if (this.state.genders && this.state.genders.length > 0) {
        params.set('genders', this.state.genders.join(','));
      }

      // Filter parameters
      if (this.state.colors.length > 0) {
        params.set('colors', this.state.colors.join(','));
      }
      if (this.state.fromPrice !== null) {
        params.set('fromPrice', this.state.fromPrice.toString());
      }
      if (this.state.toPrice !== null) {
        params.set('toPrice', this.state.toPrice.toString());
      }
      if (this.state.sizes.length > 0) {
        var expandedSizes = this.expandSizeGroups(this.state.sizes);
        params.set('sizes', expandedSizes.join(','));
      }
      if (this.state.sports.length > 0) {
        params.set('sports', this.state.sports.join(','));
      }
      if (this.state.wireTypes.length > 0) {
        params.set('wireTypes', this.state.wireTypes.join(','));
      }
      if (this.state.sustainable) {
        params.set('sustainable', 'true');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        const fetchOptions = { signal: controller.signal };

        // Add Authorization header for session-based pricing/visibility
        const authToken = this.getAuthToken();
        if (authToken) {
          fetchOptions.headers = { 'Authorization': 'Bearer ' + authToken };
        }

        const response = await fetch(SearchController.API_BASE_URL + '?' + params, fetchOptions);

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error('API error: ' + response.status);
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || 'API request failed');
        }

        console.log('SearchController: API response', {
          products: data.products?.length,
          totalCount: data.totalCount
        });


        this.saveToCache(cacheKey, data);
        return data;

      } catch (error) {
        clearTimeout(timeoutId);

        if (error.name === 'AbortError') {
          throw new Error('Request timeout - please try again');
        }

        throw error;
      }
    }

    /**
     * Get auth token for session-based pricing
     */
    getAuthToken() {
      if (typeof window.FILA_AUTH_TOKEN === 'string' && window.FILA_AUTH_TOKEN) {
        return window.FILA_AUTH_TOKEN;
      }
      var stored = localStorage.getItem('accessToken');
      if (stored) return stored;
      var match = document.cookie.match(/(?:^|;\s*)fila_auth_token=([^;]*)/);
      return match ? decodeURIComponent(match[1]) : null;
    }

    // ==========================================
    // CSR Rendering
    // ==========================================

    /**
     * Main CSR render: products array -> DOM
     */
    renderProductCards(products) {
      var sectionId = this.productGrid?.dataset.id || '';
      var fragment = document.createDocumentFragment();

      this.cacheReduceIconStates(products);

      for (var i = 0; i < products.length; i++) {
        var product = products[i];
        var li = document.createElement('li');
        li.className = 'grid__item';
        li.style.setProperty('--animation-order', i + 1);

        var options = { sectionId: sectionId, index: i, tagsList: this.tagsList };
        li.innerHTML = this.buildProductCardHTML(product, options);
        fragment.appendChild(li);
      }

      this.updateProductGrid(fragment);
      console.log('SearchController: Rendered', products.length, 'products (CSR)');
    }

    /**
     * Build complete product card HTML
     * Reproduces custom-product-card.liquid structure
     */
    buildProductCardHTML(product, options) {
      var sectionId = options.sectionId;
      var plpType = this.determinePlpType(product);
      var images = this.selectPlpImages(product, plpType);
      var priceData = this.buildPriceData(product);
      var badges = this.extractBadges(product.tags || []);
      var genderTags = this.extractGenderTags(product.tags || [], options.tagsList);
      var titlePrefix = this.getTitlePrefix(product);
      var isSoldOut = product.isSoldOut || product.displayStatus === '품절';
      var productUrl = product.url || ('/products/' + product.handle);
      var escapedTitle = this.escapeHTML(product.title || '');
      var variantsCount = (product.variants || []).length;
      var singleVariant = variantsCount <= 1;
      var hasImage = !!images.main;

      // Gender tags HTML (reused in two places)
      var self = this;
      var genderTagsHTML = genderTags.length > 0
        ? genderTags.map(function(tag) { return self.escapeHTML(tag); }).join(' | ')
        : '&nbsp;';

      // Badge HTML
      var badgeHTML = badges.label
        ? '<div class="card__badge--item" style="color: ' + this.escapeHTML(badges.color) + '">' + this.escapeHTML(badges.label.replace(/-/g, ' ')) + '</div>'
        : '';

      // Build sections
      var imageSection = this.buildImageSection(images, product, { productUrl: productUrl, escapedTitle: escapedTitle, isSoldOut: isSoldOut });
      var quickAddSection = this.buildQuickAddSection(product, priceData, { sectionId: sectionId, productUrl: productUrl, escapedTitle: escapedTitle, isSoldOut: isSoldOut, images: images });
      var swatchSection = this.buildSwatchHTML(product, plpType, options);
      var variantsJSON = this.buildVariantsJSON(product);
      var priceHTML = this.buildPriceHTML(priceData, 'card__price');

      // Sold out / Sale badge
      var bottomBadge = '';
      if (isSoldOut) {
        bottomBadge = '<span id="Badge-' + sectionId + '-' + product.id + '" class="badge badge--bottom-left color-inverse">품절</span>';
      } else if (priceData.isSale) {
        bottomBadge = '<span id="Badge-' + sectionId + '-' + product.id + '" class="badge badge--bottom-left color-accent-2">Sale</span>';
      }

      // Card wrapper data attributes
      var wrapperAttrs =
        ' data-product-card' +
        ' data-product-main-block' +
        ' data-product-handle="' + this.escapeHTML(product.handle || '') + '"' +
        ' data-product-id="' + product.id + '"' +
        ' data-display-status="' + this.escapeHTML(product.displayStatus || '') + '"' +
        (product.displayStatus === '품절' ? ' data-display-status-sold-out="true"' : '') +
        ((product.tags || []).indexOf('사원판매') >= 0 ? ' data-employee-tag="true"' : '') +
        ' data-plp-women="' + this.joinImageUrls(product.womenPlpImages || []) + '"' +
        ' data-plp-men="' + this.joinImageUrls(product.menPlpImages || []) + '"' +
        ' data-plp-common="' + this.joinImageUrls(product.commonPlpImages || []) + '"';

      return '' +
        '<div' +
        '  class="card-wrapper product-card-wrapper underline-links-hover' + (singleVariant ? ' card-wrapper_single-variant' : '') + '"' +
           wrapperAttrs +
        '>' +
        '  <div' +
        '    class="card card--standard' + (hasImage ? ' card--media' : ' card--text') + '"' +
        '    style="--ratio-percent: 133.33%;"' +
        '  >' +
        '    <div' +
        '      class="card__inner color-background-1 gradient ratio"' +
        '      style="--ratio-percent: 133.33%;"' +
        '    >' +
               imageSection +
               quickAddSection +
        '      <div class="card__content">' +
        '        <div class="card__information">' +
        '          <span class="product_gender">' + genderTagsHTML + '</span>' +
        '          <h3 class="card__heading">' +
        '            <a' +
        '              href="' + productUrl + '"' +
        '              id="StandardCardNoMediaLink-' + sectionId + '-' + product.id + '"' +
        '              class="full-unstyled-link"' +
        '            >' +
                       this.escapeHTML(titlePrefix) + escapedTitle +
        '            </a>' +
        '          </h3>' +
        '        </div>' +
        '        <div class="card__badges--wrapper">' +
                   badgeHTML +
        '        </div>' +
        '      </div>' +
        '    </div>' +
        '    <div class="card__content">' +
        '      <div class="card__content-swatch">' +
                 swatchSection +
        '      </div>' +
        '      <script type="application/json" card-data-variants>' + variantsJSON + '</script>' +
        '      <div class="card__information">' +
        '        <span class="product_gender">' + genderTagsHTML + '</span>' +
        '        <h3 class="card__heading h5" id="title-' + sectionId + '-' + product.id + '">' +
        '          <a' +
        '            href="' + productUrl + '"' +
        '            id="CardLink-' + sectionId + '-' + product.id + '"' +
        '            class="full-unstyled-link"' +
        '          >' +
                     this.escapeHTML(titlePrefix) + escapedTitle +
        '          </a>' +
        '        </h3>' +
        '        <div class="card-information">' +
                   priceHTML +
        '        </div>' +
        '      </div>' +
        '      <div class="card__badge bottom-left">' +
                 bottomBadge +
        '      </div>' +
        '    </div>' +
        '  </div>' +
        '</div>';
    }

    // ==========================================
    // PLP Type & Image Selection
    // ==========================================

    /**
     * Determine PLP image type from product tags
     * Search page has no collection gender -> tag-based fallback only
     */
    determinePlpType(product) {
      var tags = product.tags || [];
      var hasMale = false, hasFemale = false, hasNama = false, hasYeoa = false;

      for (var i = 0; i < tags.length; i++) {
        var tag = tags[i];
        if (tag === '남성' || tag === '남아') hasMale = true;
        if (tag === '여성' || tag === '여아') hasFemale = true;
        if (tag === '남아') hasNama = true;
        if (tag === '여아') hasYeoa = true;
      }

      if (hasNama || hasYeoa) return 'common';
      if (hasMale && hasFemale) return 'common';
      if (hasMale) return 'men';
      if (hasFemale) return 'women';
      return 'common';
    }

    /**
     * Select PLP images with fallback chain
     * Returns: { main: string|null, hover: string|null }
     */
    selectPlpImages(product, plpType) {
      var images;
      switch (plpType) {
        case 'women': images = product.womenPlpImages; break;
        case 'men':   images = product.menPlpImages;   break;
        default:      images = product.commonPlpImages;
      }

      if (images && images.length > 0) {
        return { main: images[0] || null, hover: images[1] || null };
      }

      // Fallback chain: common -> women -> men
      var fallbacks = [product.commonPlpImages, product.womenPlpImages, product.menPlpImages];
      for (var i = 0; i < fallbacks.length; i++) {
        var fb = fallbacks[i];
        if (fb && fb.length > 0) {
          return { main: fb[0] || null, hover: fb[1] || null };
        }
      }

      return { main: null, hover: null };
    }

    // ==========================================
    // Price
    // ==========================================

    /**
     * Build price data from server-calculated prices
     * V2 API returns session-based final price (no client calculation needed)
     */
    buildPriceData(product) {
      var price = product.price || 0;
      var compareAtPrice = product.compareAtPrice || null;
      var isSale = compareAtPrice != null && compareAtPrice > price;
      var discount = isSale ? Math.round((compareAtPrice - price) / compareAtPrice * 100) : 0;

      return { price: price, compareAtPrice: compareAtPrice, isSale: isSale, discount: discount };
    }

    /**
     * Build price display HTML
     * Simplified: no 24 session-specific data-attrs (server handles session pricing)
     */
    buildPriceHTML(priceData, priceClass) {
      var cls = 'price-display' + (priceClass ? ' ' + priceClass : '');
      var isSale = priceData.isSale;

      return '' +
        '<div class="' + cls + '" data-price="' + priceData.price + '">' +
        '  <div class="price-display__original"' + (!isSale ? ' style="display:none"' : '') + '>' +
        '    <span class="price-display__original-price">' + this.formatMoney(priceData.compareAtPrice) + '</span>' +
        '  </div>' +
        '  <div class="price-display__current">' +
        '    <span class="price-display__current-price">' + this.formatMoney(priceData.price) + '</span>' +
        '  </div>' +
        '  <div class="price-display__discount"' + (!isSale ? ' style="display:none"' : '') + '>' +
        '    <span class="price-display__discount-percentage">' + priceData.discount + '%</span>' +
        '  </div>' +
        '</div>';
    }

    // ==========================================
    // Image Section
    // ==========================================

    /**
     * Build card image section HTML
     * Handles GIF detection, srcset generation, hover image
     */
    hasESGDescriptionValue(value) {
      if (value == null) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object') {
        return this.hasESGDescriptionValue(value.value) ||
          this.hasESGDescriptionValue(value.description) ||
          this.hasESGDescriptionValue(value.esgDescription) ||
          this.hasESGDescriptionValue(value.esg_description);
      }
      return Boolean(value);
    }

    shouldShowReduceIcon(product) {
      if (!product) return false;
      if (product.sustainable) return true;

      var esg = product.esg || product.esgMetaobject || product.esg_metaobject || {};
      return this.hasESGDescriptionValue(product.esgDescription) ||
        this.hasESGDescriptionValue(product.esg_description) ||
        this.hasESGDescriptionValue(product.esgDescriptionText) ||
        this.hasESGDescriptionValue(product.esg_description_text) ||
        this.hasESGDescriptionValue(esg.description) ||
        this.hasESGDescriptionValue(esg.esgDescription) ||
        this.hasESGDescriptionValue(esg.esg_description);
    }

    cacheReduceIconStates(products) {
      this.reduceIconByHandle = {};
      if (!Array.isArray(products)) return;

      for (var i = 0; i < products.length; i++) {
        var product = products[i];
        if (product && product.handle) {
          this.reduceIconByHandle[String(product.handle).toLowerCase()] = this.shouldShowReduceIcon(product);
        }
      }
    }

    getReduceIconState(product, fallbackProduct) {
      if (!product) return false;
      if (product.handle) {
        var handle = String(product.handle).toLowerCase();
        if (Object.prototype.hasOwnProperty.call(this.reduceIconByHandle, handle)) {
          return this.reduceIconByHandle[handle];
        }
      }
      if (this.shouldShowReduceIcon(product)) return true;
      return fallbackProduct ? this.shouldShowReduceIcon(fallbackProduct) : false;
    }


    buildImageSection(images, product, opts) {
      var productUrl = opts.productUrl;
      var escapedTitle = opts.escapedTitle;
      var isSoldOut = opts.isSoldOut;

      if (!images.main) {
        return '';
      }

      var mainIsGif = images.main.indexOf('.gif') >= 0;
      var mainImgHTML;

      if (mainIsGif) {
        mainImgHTML =
          '<img' +
          ' src="' + this.escapeHTML(images.main) + '"' +
          ' alt="' + escapedTitle + '"' +
          ' class="motion-reduce"' +
          ' loading="lazy"' +
          ' data-media' +
          ' data-is-gif="true"' +
          '>';
      } else {
        mainImgHTML =
          '<img' +
          ' srcset="' + this.generateSrcset(images.main) + '"' +
          ' src="' + this.appendWidth(images.main, 533) + '"' +
          ' sizes="(min-width: 1200px) calc(50vw - 60px), (min-width: 990px) calc(50vw - 60px), (min-width: 750px) calc(33vw - 40px), calc(50vw - 18px)"' +
          ' alt="' + escapedTitle + '"' +
          ' class="motion-reduce"' +
          ' loading="lazy"' +
          ' data-media' +
          '>';
      }

      var hoverImgHTML = '';
      if (images.hover) {
        var hoverIsGif = images.hover.indexOf('.gif') >= 0;
        if (hoverIsGif) {
          hoverImgHTML =
            '<img' +
            ' src="' + this.escapeHTML(images.hover) + '"' +
            ' alt="' + escapedTitle + '"' +
            ' class="motion-reduce"' +
            ' loading="lazy"' +
            ' data-is-gif="true"' +
            '>';
        } else {
          hoverImgHTML =
            '<img' +
            ' srcset="' + this.generateSrcset(images.hover) + '"' +
            ' src="' + this.appendWidth(images.hover, 533) + '"' +
            ' sizes="(min-width: 1200px) calc(50vw - 60px), (min-width: 990px) calc(50vw - 60px), (min-width: 750px) calc(33vw - 40px), calc(50vw - 18px)"' +
            ' alt="' + escapedTitle + '"' +
            ' class="motion-reduce"' +
            ' loading="lazy"' +
            '>';
        }
      }

      var sustainableHTML = this.shouldShowReduceIcon(product)
        ? '<img class="card__reduce-icon" src="//2ikbrg-aj.myshopify.com/cdn/shop/files/Frame_1000006465_a59ab9fe-4d65-499d-9c57-c04c0130ef96.png?v=1766389935" alt="Re:Deuce" loading="lazy" width="72" height="72">'
        : '';

      return '' +
        '<div class="card__media' + (isSoldOut ? ' card__media--sold-out' : '') + '">' +
           sustainableHTML +
        '  <div class="media media--transparent media--hover-effect">' +
        '    <a href="' + productUrl + '" class="card__media-link full-unstyled-link" aria-label="' + escapedTitle + '" data-media-link>' +
               mainImgHTML +
               hoverImgHTML +
        '    </a>' +
        '  </div>' +
        '</div>';
    }

    // ==========================================
    // Swatch
    // ==========================================

    /**
     * Build swatch HTML (reproduces product-card-swatch.liquid)
     * Current product (active) + bundle products
     */
    buildSwatchHTML(product, plpType, options) {
      var productUrl = product.url || ('/products/' + product.handle);
      var isSoldOut = product.isSoldOut || product.displayStatus === '품절';
      var productImgUrls = this.buildSwatchImageUrls(product, plpType);
      var thumbUrl = this.getSwatchThumbUrl(product);
      var womenUrls = this.joinImageUrls(product.womenPlpImages || []);
      var menUrls = this.joinImageUrls(product.menPlpImages || []);
      var commonUrls = this.joinImageUrls(product.commonPlpImages || []);
      var productCodeDisplay = this.getProductCodeDisplay(product);
      var colorDisplay = this.getColorDisplay(product);
      var productReduceIcon = this.getReduceIconState(product);

      var html = '';

      // Current product swatch (active)
      html += '<div' +
        ' data-product-title="' + this.escapeHTML(product.title || '') + '"' +
        ' class="card__content-swatch-item card__content-swatch-item--active' + (isSoldOut ? ' card__content-swatch-item--unavailable' : '') + '"' +
        ' data-product-swatch' +
        ' data-product-url="' + productUrl + '"' +
        ' data-value="' + this.escapeHTML(this.extractColorName(product.tags)) + '"' +
        ' data-product-code-display="' + this.escapeHTML(productCodeDisplay) + '"' +
        ' data-color-display="' + this.escapeHTML(colorDisplay) + '"' +
        ' data-product-img="' + this.escapeHTML(productImgUrls) + '"' +
        ' data-reduce-icon="' + (productReduceIcon ? 'true' : 'false') + '"' +
        ' data-display-status="' + this.escapeHTML(product.displayStatus || '') + '"' +
        ' data-plp-women="' + this.escapeHTML(womenUrls) + '"' +
        ' data-plp-men="' + this.escapeHTML(menUrls) + '"' +
        ' data-plp-common="' + this.escapeHTML(commonUrls) + '"' +
        '>';

      if (thumbUrl) {
        html += '<img src="' + this.escapeHTML(thumbUrl) + '" alt="' + this.escapeHTML(product.title || '') + '" class="card__content-swatch-item-image" width="56" height="56">';
      } else {
        html += '<div class="card__content-swatch-item-image" aria-label="' + this.escapeHTML(product.title || '') + '" style="background-color: var(--swatch--background, #f0f0f0);"></div>';
      }
      html += '</div>';

      // Bundle products (other colors)
      var bundleProducts = product.bundleProducts || [];
      for (var i = 0; i < bundleProducts.length; i++) {
        var bp = bundleProducts[i];
        if (bp.id === product.id) continue;
        if (!this.shouldShowBundleOnSearch(bp.tags || [], this.state.category)) continue;

        var bpSoldOut = !bp.available || bp.displayStatus === '품절';
        var bpUrl = bp.url || ('/products/' + bp.handle);
        var bpImgUrls = this.buildSwatchImageUrls(bp, plpType);
        var bpThumbUrl = this.getSwatchThumbUrl(bp);
        var bpWomenUrls = this.joinImageUrls(bp.womenPlpImages || []);
        var bpMenUrls = this.joinImageUrls(bp.menPlpImages || []);
        var bpCommonUrls = this.joinImageUrls(bp.commonPlpImages || []);
        var bpProductCodeDisplay = this.getProductCodeDisplay(bp);
        var bpColorDisplay = this.getColorDisplay(bp);
        var bpReduceIcon = this.getReduceIconState(bp, product);

        html += '<div' +
          ' data-product-title="' + this.escapeHTML(bp.title || '') + '"' +
          ' class="card__content-swatch-item' + (bpSoldOut ? ' card__content-swatch-item--unavailable' : '') + '"' +
          ' data-product-swatch' +
          ' data-product-url="' + bpUrl + '"' +
          ' data-value="' + this.escapeHTML(this.extractColorName(bp.tags)) + '"' +
          ' data-product-code-display="' + this.escapeHTML(bpProductCodeDisplay) + '"' +
          ' data-color-display="' + this.escapeHTML(bpColorDisplay) + '"' +
          ' data-product-img="' + this.escapeHTML(bpImgUrls) + '"' +
          ' data-reduce-icon="' + (bpReduceIcon ? 'true' : 'false') + '"' +
          ' data-display-status="' + this.escapeHTML(bp.displayStatus || '') + '"' +
          ' data-plp-women="' + this.escapeHTML(bpWomenUrls) + '"' +
          ' data-plp-men="' + this.escapeHTML(bpMenUrls) + '"' +
          ' data-plp-common="' + this.escapeHTML(bpCommonUrls) + '"' +
          '>';

        if (bpThumbUrl) {
          html += '<img src="' + this.escapeHTML(bpThumbUrl) + '" alt="' + this.escapeHTML(bp.title || '') + '" class="card__content-swatch-item-image" width="56" height="56" loading="lazy">';
        } else {
          html += '<div class="card__content-swatch-item-image" aria-label="' + this.escapeHTML(bp.title || '') + '" style="background-color: var(--swatch--background, #f0f0f0);"></div>';
        }
        html += '</div>';
      }

      return html;
    }

    /**
     * Build comma-separated image URLs for swatch data-product-img
     * Uses width=450 (matches product-card-swatch.liquid)
     */
    buildSwatchImageUrls(product, plpType) {
      var images;
      switch (plpType) {
        case 'women': images = product.womenPlpImages; break;
        case 'men':   images = product.menPlpImages;   break;
        default:      images = product.commonPlpImages;
      }

      if (images && images.length > 0) {
        var self = this;
        return images.map(function(url) {
          return url.indexOf('.gif') >= 0 ? url : self.appendWidth(url, 450);
        }).join(',');
      }

      // Fallback
      var fallbacks = [product.commonPlpImages, product.womenPlpImages, product.menPlpImages];
      for (var i = 0; i < fallbacks.length; i++) {
        var fb = fallbacks[i];
        if (fb && fb.length > 0) {
          var self2 = this;
          return fb.map(function(url) {
            return url.indexOf('.gif') >= 0 ? url : self2.appendWidth(url, 450);
          }).join(',');
        }
      }

      return '';
    }

    /**
     * Get swatch thumbnail URL (100x100)
     * Uses first available PLP image
     */
    getSwatchThumbUrl(product) {
      var img = (product.images && product.images.length > 0) ? product.images[0] : '';
      if (!img) return '';
      if (img.indexOf('.gif') >= 0) return img;
      return this.appendWidth(img, 100);
    }

    // ==========================================
    // Quick Add Section
    // ==========================================

    /**
     * Build Quick Add section HTML
     * Reproduces the mobile button + option container from custom-product-card.liquid
     */
    buildQuickAddSection(product, priceData, opts) {
      var sectionId = opts.sectionId;
      var escapedTitle = opts.escapedTitle;
      var isSoldOut = opts.isSoldOut;
      var images = opts.images;
      var variants = product.variants || [];
      var singleVariant = variants.length <= 1;
      var firstVariant = variants.find(function(v) { return !v.isSoldOut; }) || variants[0];
      var firstVariantId = firstVariant ? firstVariant.id : '';
      var firstVariantDisabled = isSoldOut || (firstVariant ? firstVariant.isSoldOut : true);
      var productFormId = 'quick-add-' + sectionId + product.id;
      var thumbUrl = images.main ? this.appendWidth(images.main, 200) : '';

      // Size options
      var sizeOptionsHTML = '';
      var sizeVariants = variants.filter(function(v) { return v.size && v.size !== 'Default Title'; });
      sizeVariants.sort(function(a, b) { return compareSizes(a.size, b.size); });
      if (sizeVariants.length > 0) {
        var self = this;
        sizeOptionsHTML =
          '<h3 class="card__content-option-list-heading">SELECT SIZE</h3>' +
          '<ul class="card__content-option-list">' +
            sizeVariants.map(function(v) {
              var unavailable = v.isSoldOut || isSoldOut;
              return '<li' +
                ' class="card__content-option-item' + (unavailable ? ' card__content-option-item--unavailable' : '') + '"' +
                ' data-option-value="' + self.escapeHTML(v.size) + '"' +
                ' role="button"' +
                ' tabindex="0"' +
                ' aria-label="' + self.escapeHTML(v.size) + '"' +
                '>' + self.escapeHTML(v.size) + '</li>';
            }).join('') +
          '</ul>';
      }

      var quickAddPriceHTML = this.buildPriceHTML(priceData, 'card__price price-display--inline');
      var productDisplayHTML = this.buildProductDisplayHTML(product);

      return '' +
        '<button class="card_content--mobile-btn' + (singleVariant ? ' card_content--mobile-btn-hidden' : '') + '" data-mobile-btn>' +
        '  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" class="quick-add__submit--btn-icon">' +
        '    <g clip-path="url(#clip0_3098_4340)"><rect width="32" height="32" rx="16" fill="black" fill-opacity="0.6"/>' +
        '    <g clip-path="url(#clip1_3098_4340)"><path d="M22.3962 12.9263H9.60547V22.2286H22.3962V12.9263Z" stroke="white" stroke-width="1.2"/>' +
        '    <path d="M16.0007 9.43793C16.3825 9.43793 16.7605 9.51312 17.1132 9.65921C17.4659 9.8053 17.7863 10.0194 18.0563 10.2894C18.3262 10.5593 18.5403 10.8798 18.6864 11.2325C18.8325 11.5851 18.9077 11.9632 18.9077 12.3449V12.9263H13.0938V12.3449C13.0938 11.5739 13.4 10.8345 13.9452 10.2894C14.4903 9.7442 15.2297 9.43793 16.0007 9.43793Z" stroke="white" stroke-width="1.2"/></g>' +
        '    <rect x="18.6172" y="7.98444" width="6.97674" height="6.97674" rx="3.48837" fill="white"/>' +
        '    <path d="M21.0561 11.0091C21.0561 9.81869 21.5707 9.43497 22.1724 9.43497C22.7829 9.43497 23.2974 9.81869 23.2974 11.0091C23.2974 12.2257 22.7829 12.6094 22.1724 12.6094C21.5707 12.6094 21.0561 12.2257 21.0561 11.0091ZM21.6753 11.0091C21.6753 11.3536 21.6753 12.0992 22.1724 12.0992C22.6782 12.0992 22.6782 11.3536 22.6782 11.0091C22.6782 10.6908 22.6782 9.94514 22.1724 9.94514C21.6753 9.94514 21.6753 10.6908 21.6753 11.0091Z" fill="white"/>' +
        '    <g clip-path="url(#clip2_3098_4340)"><path d="M22.1055 10.201V12.7446" stroke="#626262" stroke-width="0.8" stroke-linecap="square" stroke-linejoin="round"/>' +
        '    <path d="M20.832 11.4728H23.3756" stroke="#626262" stroke-width="0.8" stroke-linecap="square" stroke-linejoin="round"/></g></g>' +
        '  </svg>' +
        '</button>' +
        '<div' +
        '  class="card__content-option-container' + (singleVariant ? ' card__content-option-container-mobile' : '') + '"' +
        '  data-swatch-container' +
        '>' +
        '  <div class="card__content-drag-handle">' +
        '    <span class="card__content-drag-handle-bar"></span>' +
        '  </div>' +
        '  <div class="card__content-option-container-loading"></div>' +
        '  <div class="card__content-close--btn" data-mobile-close></div>' +
        '  <div class="card__content-btn">' +
        '    <product-form data-section-id="' + sectionId + '">' +
        '      <form method="post" action="/cart/add" accept-charset="UTF-8" class="form" novalidate="novalidate" data-type="add-to-cart-form">' +
        '        <input type="hidden" name="form_type" value="product">' +
        '        <input type="hidden" name="utf8" value="✓">' +
        '        <input' +
        '          type="hidden"' +
        '          name="id"' +
        '          value="' + firstVariantId + '"' +
        '          class="product-variant-id"' +
                   (firstVariantDisabled ? ' disabled' : '') +
        '        >' +
        '        <button' +
        '          id="' + productFormId + '-submit"' +
        '          type="button"' +
        '          name="add"' +
        '          class="cart-button quick-add__submit quick-add__submit--btn button button--full-width button--secondary"' +
        '          data-no-set-draw' +
        '          aria-haspopup="dialog"' +
        '          aria-live="polite"' +
        '          data-variant-id="' + firstVariantId + '"' +
        '          data-quantity="1"' +
        '          data-sold-out-message="true"' +
                   (firstVariantDisabled ? ' disabled' : '') +
        '        >' +
        '          <span class="quick-add__submit--btn-text' + (firstVariantDisabled ? ' hidden' : '') + '" data-quick-add-text>' +
                     (singleVariant ? '<span class="quick-add__submit--btn-text-single">' : '') +
        '            QUICK ADD' +
        '            <svg width="27" height="24" viewBox="0 0 27 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="quick-add__submit--cart-icon"><path d="M23 7H1V23H23V7Z" stroke="white" stroke-width="2"/><path d="M12 1C12.6566 1 13.3068 1.12933 13.9134 1.3806C14.52 1.63188 15.0712 2.00017 15.5355 2.46447C15.9998 2.92876 16.3681 3.47995 16.6194 4.08658C16.8707 4.69321 17 5.34339 17 6V7H7V6C7 4.67392 7.52678 3.40215 8.46447 2.46447C9.40215 1.52678 10.6739 1 12 1Z" stroke="white" stroke-width="2"/><rect x="16.3594" width="9.83807" height="9.83807" rx="4.91904" fill="white"/><g clip-path="url(#clip_cart_qa_' + product.id + ')"><path d="M21.2812 3.12598V6.71277" stroke="#626262" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/><path d="M19.4844 4.91895H23.0712" stroke="#626262" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/></g><defs><clipPath id="clip_cart_qa_' + product.id + '"><rect width="6.1488" height="6.1488" fill="white" transform="translate(18.207 1.84473)"/></clipPath></defs></svg>' +
                     (singleVariant ? '</span>' : '') +
        '          </span>' +
        '          <span class="sold-out-message hidden" data-sold-out-message>품절</span>' +
        '        </button>' +
        '      </form>' +
        '    </product-form>' +
        '  </div>' +
        '  <div class="card__content-option-product-info">' +
        '    <div class="card__content-option-product-image">' +
               (thumbUrl ? '<img src="' + this.escapeHTML(thumbUrl) + '" alt="' + escapedTitle + '" data-option-product-image loading="lazy">' : '') +
        '    </div>' +
        '    <div class="card__content-option-product-details">' +
        '      <div class="card__content-option-product-title">' + escapedTitle + '</div>' +
        '      <div class="card__content-option-product-color" data-option-product-color>' + productDisplayHTML + '</div>' +
        '      <div class="card__content-option-product-price" data-option-product-price>' +
                 quickAddPriceHTML +
        '      </div>' +
        '    </div>' +
        '  </div>' +
             sizeOptionsHTML +
        '</div>';
    }

    // ==========================================
    // Variants JSON
    // ==========================================

    /**
     * Build card-data-variants JSON for inventory-validation.js
     * Format: [{"id": "string", "title": "size", "available": bool, "is_sold_out": bool}]
     */
    buildVariantsJSON(product) {
      var variants = [];
      var productVariants = product.variants || [];
      var bundleProducts = product.bundleProducts || [];

      // Check if current product is in its own bundle
      var currentInBundle = false;
      for (var ci = 0; ci < bundleProducts.length; ci++) {
        if (bundleProducts[ci].id === product.id) {
          currentInBundle = true;
          break;
        }
      }

      var productUrl = product.url || ('/products/' + product.handle);

      // If has bundle products
      if (bundleProducts.length > 0) {
        // Add current product variants first (if not already in bundle)
        if (!currentInBundle) {
          for (var pi = 0; pi < productVariants.length; pi++) {
            var v = productVariants[pi];
            variants.push({
              id: String(v.id),
              title: this.extractColorName(product.tags) + ', ' + (v.size || ''),
              available: !v.isSoldOut,
              is_sold_out: v.isSoldOut || false,
              product_url: productUrl
            });
          }
        }

        // Add bundle product variants
        for (var b = 0; b < bundleProducts.length; b++) {
          var bp = bundleProducts[b];
          var bpUrl = bp.url || ('/products/' + bp.handle);
          var bpVariants = bp.variants || [];
          for (var j = 0; j < bpVariants.length; j++) {
            var bv = bpVariants[j];
            variants.push({
              id: String(bv.id),
              title: this.extractColorName(bp.tags) + ', ' + (bv.size || ''),
              available: !bv.isSoldOut,
              is_sold_out: bv.isSoldOut || false,
              product_url: bpUrl
            });
          }
        }
      } else {
        // No bundle: current product variants only
        for (var k = 0; k < productVariants.length; k++) {
          var pv = productVariants[k];
          variants.push({
            id: String(pv.id),
            title: this.extractColorName(product.tags) + ', ' + (pv.size || ''),
            available: !pv.isSoldOut,
            is_sold_out: pv.isSoldOut || false,
            product_url: productUrl
          });
        }
      }

      return JSON.stringify(variants);
    }

    // ==========================================
    // Tag & Badge Helpers
    // ==========================================

    /**
     * Extract badge label and color from tags
     * Tags format: "label-NEW", "label-color-red"
     */
    extractColorName(tags) {
      if (!tags) return '';
      for (var i = 0; i < tags.length; i++) {
        var tag = tags[i];
        if (typeof tag === 'string' && tag.indexOf('color:') === 0) {
          var parts = tag.split(':');
          return parts[1] || '';
        }
      }
      return '';
    }

    extractBadges(tags) {
      var label = '';
      var color = 'white';

      for (var i = 0; i < tags.length; i++) {
        var tag = tags[i];
        if (tag.indexOf('label-') === 0 && tag.indexOf('label-color') !== 0) {
          label = tag.substring(6); // remove 'label-'
        }
        if (tag.indexOf('label-color-') === 0) {
          color = tag.substring(12); // remove 'label-color-'
        }
      }

      return { label: label, color: color };
    }

    /**
     * Extract gender tags for display
     * Reproduces custom-product-card.liquid gender tag logic
     */
    extractGenderTags(tags, tagsList) {
      if (!tagsList || tagsList.length === 0) return [];
      if (!tags) tags = [];

      var hasGongyong = tags.indexOf('공용') >= 0;
      var hasNamseong = tags.indexOf('남성') >= 0;
      var hasYeoseong = tags.indexOf('여성') >= 0;
      var hasNama = tags.indexOf('남아') >= 0;
      var hasYeoa = tags.indexOf('여아') >= 0;
      // '공용' 태그가 없어도 남성+여성을 모두 가지면 '공용'으로 합쳐서 노출
      var inferGongyong = !hasGongyong && hasNamseong && hasYeoseong;
      var showKids = hasNama && hasYeoa;

      // tagsList(커스터마이저 설정) 순서대로 순회해야 노출 순서가 보장된다.
      // 상품 태그 순서(Shopify 반환 순서)에 의존하면 순서를 제어할 수 없음.
      var result = [];
      var seen = {};
      for (var i = 0; i < tagsList.length; i++) {
        var tag = tagsList[i];
        if (!tag) continue;

        var include = tags.indexOf(tag) >= 0;

        if (tag === '공용' && inferGongyong) include = true;
        if ((tag === '남성' || tag === '여성') && (hasGongyong || inferGongyong)) include = false;
        if ((tag === '남아' || tag === '여아') && showKids) include = false;
        if (tag === '키즈') {
          if (showKids) include = true;
          else if (hasNama || hasYeoa) include = false;
        }

        if (!include || seen[tag]) continue;
        seen[tag] = true;
        result.push(tag);
      }

      // tagsList에 '공용'/'키즈'가 없어도 파생 태그는 노출한다 (Liquid 구현체와 동일).
      // 리스트에 있으면 위 루프에서 이미 순서대로 담겼으므로 건너뛴다.
      // '공용' 판정은 inferGongyong이 아니라 남성+여성 동시 보유 여부 (기존 동작 보존)
      if (hasNamseong && hasYeoseong && tagsList.indexOf('공용') < 0) result.push('공용');
      if (showKids && tagsList.indexOf('키즈') < 0) result.push('키즈');

      return result;
    }

    shouldShowBundleOnSearch(bpTags, category) {
      if (!category || category === 'all') return true;
      var hasGongyong = bpTags.indexOf('공용') >= 0;
      if (hasGongyong) return true;
      var hasMale = bpTags.indexOf('남성') >= 0 || bpTags.indexOf('남아') >= 0;
      var hasFemale = bpTags.indexOf('여성') >= 0 || bpTags.indexOf('여아') >= 0;
      if (hasMale && hasFemale) return true;
      var isWomen = category === '여성' || category === '여아';
      var isMen = category === '남성' || category === '남아';
      if (isWomen && hasMale && !hasFemale) return false;
      if (isMen && hasFemale && !hasMale) return false;
      return true;
    }

    /**
     * Get title prefix (with expiry date check)
     */
    getTitlePrefix(product) {
      var prefix = product.productPrefix || '';
      if (!prefix) return '';

      var endDate = product.productPrefixEndDate;
      if (endDate) {
        var now = Date.now();
        var end = new Date(endDate).getTime();
        if (now > end) return '';
      }

      return prefix;
    }

    // ==========================================
    // Image Helpers
    // ==========================================

    /**
     * Generate srcset string for responsive images
     * Sizes: [165, 360, 533, 720, 940, 1066]
     */
    generateSrcset(imageUrl) {
      if (!imageUrl) return '';

      var parts = [];
      var widths = SearchController.SRCSET_WIDTHS;
      for (var i = 0; i < widths.length; i++) {
        parts.push(this.appendWidth(imageUrl, widths[i]) + ' ' + widths[i] + 'w');
      }
      // Add original as largest
      parts.push(this.escapeHTML(imageUrl) + ' 1500w');
      return parts.join(', ');
    }

    /**
     * Append width parameter to Shopify CDN URL
     */
    appendWidth(url, width) {
      if (!url) return '';
      var escaped = this.escapeHTML(url);
      var separator = escaped.indexOf('?') >= 0 ? '&' : '?';
      return escaped + separator + 'width=' + width;
    }

    /**
     * Join image URLs with comma
     */
    joinImageUrls(images) {
      if (!images || images.length === 0) return '';
      return images.join(',');
    }

    // ==========================================
    // Utility
    // ==========================================

    /**
     * Escape HTML to prevent XSS
     */
    escapeHTML(str) {
      if (str == null) return '';
      if (typeof str !== 'string') str = String(str);
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    getProductCodeDisplay(product) {
      product = product || {};
      var rawCode = product.oms_cd || product.omsCd || product.productCode || product.handle || '';
      rawCode = String(rawCode).trim();
      if (!rawCode) return '';

      var displayCode = rawCode;
      var brandCode = String(product.brnd_cd || product.brndCd || '').trim();
      if (brandCode && displayCode.indexOf(brandCode) === 0) {
        displayCode = displayCode.slice(brandCode.length);
      } else {
        var knownPrefixes = ['1100', '1170', '1200'];
        for (var i = 0; i < knownPrefixes.length; i++) {
          if (displayCode.indexOf(knownPrefixes[i]) === 0) {
            displayCode = displayCode.slice(knownPrefixes[i].length);
            break;
          }
        }
      }
      return displayCode.toUpperCase();
    }

    getColorDisplay(product) {
      product = product || {};
      var colorName = product.clr_info || product.clrInfo || this.extractColorName(product.tags || []) || '';
      colorName = String(colorName).trim();
      if (!colorName) return '';

      var rawCode = String(product.oms_cd || product.omsCd || product.productCode || product.handle || '').trim();
      var colorCode = String(product.clr_cd || product.clrCd || '').trim();
      if (!colorCode && rawCode) colorCode = rawCode.slice(-3);
      colorCode = colorCode.toUpperCase();
      if (!colorCode || colorName.indexOf(colorCode) >= 0) return colorName;
      return colorCode + ' (' + colorName + ')';
    }

    buildProductDisplayHTML(product) {
      var productCode = this.getProductCodeDisplay(product);
      var colorDisplay = this.getColorDisplay(product);
      var html = '';
      if (productCode) html += '<div class="card__content-option-product-code">상품코드: ' + this.escapeHTML(productCode) + '</div>';
      if (colorDisplay) html += '<div class="card__content-option-product-color-line">색상: ' + this.escapeHTML(colorDisplay) + '</div>';
      return html;
    }

    formatMoney(price) {
      if (price == null || isNaN(price)) return '0원';
      var amount = Math.round(price);
      return amount.toLocaleString('ko-KR') + '원';
    }

    // ==========================================
    // DOM Updates
    // ==========================================

    /**
     * Update product grid DOM with CSR-generated elements
     */
    updateProductGrid(fragment) {
      var grid = this.productGrid;
      if (!grid) return;

      var ul = grid.querySelector('.product-grid');
      if (!ul) return;

      // Clear everything (skeletons, existing products, empty/error states)
      ul.innerHTML = '';

      // Remove empty/error states outside the UL
      var emptyStates = grid.querySelectorAll('.search__empty-state, .search__error-state');
      for (var i = 0; i < emptyStates.length; i++) {
        emptyStates[i].remove();
      }

      // Append CSR-generated fragment
      ul.appendChild(fragment);

      grid.classList.add('loaded');
      grid.classList.remove('loading');
    }

    /**
     * Update product count display
     */
    updateProductCount(totalCount) {
      var count = totalCount || 0;
      // Update active tab count (product tab)
      var productTabCount = document.querySelector('[data-tab-count="product"]');
      if (productTabCount) {
        productTabCount.textContent = count;
      }
    }

    // ==========================================
    // Pagination
    // ==========================================

    /**
     * Update pagination UI
     */
    updatePagination() {
      var container = document.getElementById('search-pagination');
      if (!container) return;

      if (this.state.totalPages <= 1) {
        container.style.display = 'none';
        container.innerHTML = '';
        return;
      }

      container.style.display = '';
      container.innerHTML = this.buildPaginationHTML();
      this.setupPaginationListeners();
    }

    /**
     * Build pagination HTML
     */
    buildPaginationHTML() {
      var page = this.state.page;
      var totalPages = this.state.totalPages;

      var html = '<nav class="pagination" role="navigation" aria-label="Pagination">';
      html += '<ul class="pagination__list list-unstyled" role="list">';

      // Previous button
      if (page > 1) {
        html += '<li>' +
          '<button type="button" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="Previous page" data-page="' + (page - 1) + '">' +
          '  <span class="svg-wrapper">' +
          '    <svg class="icon icon-caret" viewBox="0 0 10 6">' +
          '      <path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/>' +
          '    </svg>' +
          '  </span>' +
          '</button></li>';
      }

      // Page numbers
      var maxVisible = 5;
      var start = Math.max(1, page - Math.floor(maxVisible / 2));
      var end = Math.min(totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      if (start > 1) {
        html += '<li><button type="button" class="pagination__item link" data-page="1">1</button></li>';
        if (start > 2) {
          html += '<li><span class="pagination__item">...</span></li>';
        }
      }

      for (var i = start; i <= end; i++) {
        if (i === page) {
          html += '<li><span class="pagination__item pagination__item--current light" aria-current="page">' + i + '</span></li>';
        } else {
          html += '<li><button type="button" class="pagination__item link" data-page="' + i + '">' + i + '</button></li>';
        }
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          html += '<li><span class="pagination__item">...</span></li>';
        }
        html += '<li><button type="button" class="pagination__item link" data-page="' + totalPages + '">' + totalPages + '</button></li>';
      }

      // Next button
      if (page < totalPages) {
        html += '<li>' +
          '<button type="button" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Next page" data-page="' + (page + 1) + '">' +
          '  <span class="svg-wrapper">' +
          '    <svg class="icon icon-caret" viewBox="0 0 10 6">' +
          '      <path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/>' +
          '    </svg>' +
          '  </span>' +
          '</button></li>';
      }

      html += '</ul></nav>';
      return html;
    }

    /**
     * Setup pagination click listeners
     */
    setupPaginationListeners() {
      var self = this;
      var container = document.getElementById('search-pagination');
      if (!container) return;

      var buttons = container.querySelectorAll('button[data-page]');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].addEventListener('click', function(e) {
          e.preventDefault();
          var page = parseInt(this.dataset.page, 10);
          if (!isNaN(page)) {
            self.handlePageChange(page);
          }
        });
      }
    }

    // ==========================================
    // State Management
    // ==========================================

    /**
     * Restore state from URL parameters
     */
    restoreStateFromURL() {
      var params = new URLSearchParams(window.location.search);

      if (params.has('q')) {
        this.state.keyword = params.get('q');
      }
      if (params.has('filter.p.tag')) {
        this.state.category = params.get('filter.p.tag');
      }
      if (params.has('sort_by')) {
        this.state.sortBy = params.get('sort_by');
      }
      if (params.has('page')) {
        this.state.page = parseInt(params.get('page'), 10) || 1;
      }
      // Filter params
      if (params.has('colors')) {
        this.state.colors = params.get('colors').split(',').filter(Boolean);
      }
      if (params.has('fromPrice')) {
        this.state.fromPrice = parseFloat(params.get('fromPrice'));
      }
      if (params.has('toPrice')) {
        this.state.toPrice = parseFloat(params.get('toPrice'));
      }
      if (params.has('genders')) {
        this.state.genders = params.get('genders').split(',').filter(Boolean);
      }
      if (params.has('sizes')) {
        this.state.sizes = params.get('sizes').split(',').filter(Boolean);
      }
      if (params.has('sports')) {
        this.state.sports = params.get('sports').split(',').filter(Boolean);
      }
      if (params.has('wireTypes')) {
        this.state.wireTypes = params.get('wireTypes').split(',').filter(Boolean);
      }
      if (params.has('sustainable')) {
        this.state.sustainable = params.get('sustainable') === 'true';
      }

      // Update sort dropdown to match URL state
      this.updateSortDropdown();
    }

    /**
     * Update URL with current state (without page reload)
     */
    updateURL() {
      var params = new URLSearchParams();

      if (this.state.keyword) {
        params.set('q', this.state.keyword);
      }
      if (this.state.category && this.state.category !== 'all') {
        params.set('filter.p.tag', this.state.category);
      }
      if (this.state.sortBy !== 'new') {
        params.set('sort_by', this.state.sortBy);
      }
      if (this.state.page > 1) {
        params.set('page', this.state.page.toString());
      }
      // Filter params
      if (this.state.colors.length > 0) {
        params.set('colors', this.state.colors.join(','));
      }
      if (this.state.fromPrice !== null) {
        params.set('fromPrice', this.state.fromPrice.toString());
      }
      if (this.state.toPrice !== null) {
        params.set('toPrice', this.state.toPrice.toString());
      }
      if (this.state.genders.length > 0) {
        params.set('genders', this.state.genders.join(','));
      }
      if (this.state.sizes.length > 0) {
        params.set('sizes', this.state.sizes.join(','));
      }
      if (this.state.sports.length > 0) {
        params.set('sports', this.state.sports.join(','));
      }
      if (this.state.wireTypes.length > 0) {
        params.set('wireTypes', this.state.wireTypes.join(','));
      }
      if (this.state.sustainable) {
        params.set('sustainable', 'true');
      }

      var newUrl = params.toString()
        ? window.location.pathname + '?' + params
        : window.location.pathname;

      history.pushState({ ...this.state }, '', newUrl);
    }

    /**
     * Update sort dropdown to match current state
     */
    updateSortDropdown() {
      var sortSelect = document.querySelector('[data-search-sort]');
      if (sortSelect && sortSelect.value !== this.state.sortBy) {
        sortSelect.value = this.state.sortBy;
      }
      // Update sort label text
      var sortNames = { 'new': '신상품순', 'review': '리뷰순', 'sale': '판매순', 'price': '낮은가격순', '-price': '높은가격순' };
      var label = document.querySelector('[data-search-sort-label]');
      if (label) label.textContent = sortNames[this.state.sortBy] || '신상품순';
      // Update radio buttons in drawer
      var radios = document.querySelectorAll('[data-search-sort-list] input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        radios[i].checked = radios[i].value === this.state.sortBy;
      }
    }

    // ==========================================
    // Event Listeners
    // ==========================================

    /**
     * Setup event listeners
     */
    setupEventListeners() {
      var self = this;

      // Sort change
      document.addEventListener('change', function(e) {
        if (e.target.matches('[data-search-sort]')) {
          e.preventDefault();
          e.stopPropagation();
          self.handleSortChange(e.target.value);
        }
      });

      // Search filter drawer toggle
      var trigger = document.querySelector('[data-search-filter-drawer-trigger]');
      var wrapper = document.querySelector('[data-search-filter-drawer-wrapper]');
      var backdrop = document.querySelector('[data-search-filter-drawer-backdrop]');
      var closeBtn = document.querySelector('[data-search-filter-drawer-close]');

      function openSearchDrawer() {
        if (wrapper) { wrapper.classList.add('is-open'); document.body.style.overflow = 'hidden'; }
      }
      function closeSearchDrawer() {
        if (wrapper) { wrapper.classList.remove('is-open'); document.body.style.overflow = ''; }
      }

      if (trigger) trigger.addEventListener('click', openSearchDrawer);
      if (backdrop) backdrop.addEventListener('click', closeSearchDrawer);
      if (closeBtn) closeBtn.addEventListener('click', closeSearchDrawer);

      // Color filter
      document.addEventListener('change', function(e) {
        if (e.target.classList.contains('color-filter__checkbox')) {
          e.stopPropagation();
          self.handleColorFilterChange(e.target);
        }
      });

      // Gender filter
      document.addEventListener('change', function(e) {
        if (e.target.classList.contains('gender-filter__checkbox')) {
          e.stopPropagation();
          self.handleGenderFilterChange(e.target);
        }
      });

      // Size filter (chip click)
      document.addEventListener('click', function(e) {
        var chip = e.target.closest('.size-chip');
        if (chip) {
          e.preventDefault();
          e.stopPropagation();
          self.handleSizeFilterChange(chip);
        }
      });

      // Sports filter
      document.addEventListener('change', function(e) {
        if (e.target.classList.contains('sports-filter__checkbox')) {
          e.stopPropagation();
          self.handleSportsFilterChange(e.target);
        }
      });

      // Wire filter
      document.addEventListener('change', function(e) {
        if (e.target.classList.contains('wire-filter__checkbox')) {
          e.stopPropagation();
          self.handleWireFilterChange(e.target);
        }
      });

      // Sustainable filter
      document.addEventListener('change', function(e) {
        if (e.target.classList.contains('sustainable-filter__checkbox')) {
          e.stopPropagation();
          self.handleSustainableFilterChange(e.target);
        }
      });

      // Active facet badge remove
      document.addEventListener('click', function(e) {
        var badge = e.target.closest('[data-facet-type]');
        if (badge) self.handleFacetRemove(badge.dataset.facetType, badge.dataset.facetValue);
      });

      // Clear all filters (PLP reset button + drawer reset)
      document.addEventListener('click', function(e) {
        var isPlpReset = e.target.id === 'facets-reset-plp' || e.target.closest('#facets-reset-plp');
        if (isPlpReset) { e.preventDefault(); e.stopPropagation(); self.clearAllFilters(); }
      }, true);
      document.addEventListener('click', function(e) {
        if (e.target.closest('[data-clear-all-filters]')) {
          e.preventDefault();
          e.stopPropagation();
          self.clearAllFilters();
        }
      });

      // Price slider
      this.setupPriceSlider();

      // Browser back/forward
      window.addEventListener('popstate', function(e) {
        self.restoreStateFromURL();
        if (self.state.keyword) {
          self.fetchAndRender();
        }
      });
    }

    /**
     * Handle sort change
     */
    async handleSortChange(sortBy) {
      if (this.isLoading) {
        this.updateSortDropdown();
        return;
      }

      this.state.sortBy = sortBy;
      this.state.page = 1;
      this.updateURL();
      this.updateSortDropdown();
      await this.fetchAndRender();
    }

    /**
     * Handle page change
     */
    async handlePageChange(page) {
      if (this.isLoading) return;

      this.state.page = page;
      this.updateURL();
      this.scrollToTop();
      await this.fetchAndRender();
    }

    /**
     * Handle category tab change (called from main-search.js)
     */
    async handleCategoryChange(category) {
      if (this.isLoading) return;

      this.state.category = category;
      this.state.page = 1;
      // Reset filters on category change
      this.state.colors = [];
      this.state.fromPrice = null;
      this.state.toPrice = null;
      this.state.genders = [];
      this.state.sizes = [];
      this.state.sports = [];
      this.state.wireTypes = [];
      this.state.sustainable = false;
      this.resetFilterUI();
      this.updateURL();
      await this.fetchAndRender();
    }

    /**
     * Handle keyword change (called from main-search.js on form submit)
     */
    async handleKeywordChange(keyword, category) {
      if (this.isLoading) return;

      this.state.keyword = keyword;
      if (category) {
        this.state.category = category;
      }
      this.state.page = 1;
      this.state.sortBy = 'new';
      // Reset filters on keyword change
      this.state.colors = [];
      this.state.fromPrice = null;
      this.state.toPrice = null;
      this.state.genders = [];
      this.state.sizes = [];
      this.state.sports = [];
      this.state.wireTypes = [];
      this.state.sustainable = false;
      this.resetFilterUI();
      this.updateSortDropdown();
      this.updateURL();

      // Update search results header
      var titleEl = document.querySelector('.search-results-title h1');
      if (titleEl) {
        titleEl.textContent = keyword;
      }

      if (keyword) {
        await this.fetchAndRender();
      } else {
        this.showEmptyState('검색어를 입력해주세요.');
      }
    }

    // ==========================================
    // UI State
    // ==========================================

    /**
     * Show loading state
     */
    showLoadingState() {
      if (this.productGrid) {
        this.productGrid.classList.add('loading');
      }
    }

    /**
     * Hide loading state
     */
    hideLoadingState() {
      if (this.productGrid) {
        this.productGrid.classList.remove('loading');
      }
    }

    /**
     * Show empty state
     */
    showEmptyState(message) {
      var grid = this.productGrid;
      if (!grid) return;

      var ul = grid.querySelector('.product-grid');
      if (ul) {
        ul.innerHTML = '';
      }

      // Remove existing states
      var existing = grid.querySelectorAll('.search__empty-state, .search__error-state');
      for (var i = 0; i < existing.length; i++) {
        existing[i].remove();
      }

      var emptyDiv = document.createElement('div');
      emptyDiv.className = 'search__empty-state';
      emptyDiv.innerHTML = '<p>' + (message || '검색결과가 없습니다.') + '</p>';
      grid.appendChild(emptyDiv);

      grid.classList.add('loaded');
      grid.classList.remove('loading');

      // Update count to 0
      this.updateProductCount(0);

      // Hide pagination
      var paginationContainer = document.getElementById('search-pagination');
      if (paginationContainer) {
        paginationContainer.style.display = 'none';
        paginationContainer.innerHTML = '';
      }
    }

    /**
     * Show error state
     */
    showErrorState(message) {
      var grid = this.productGrid;
      if (!grid) return;

      var ul = grid.querySelector('.product-grid');
      if (ul) {
        ul.innerHTML = '';
      }

      // Remove existing states
      var existing = grid.querySelectorAll('.search__empty-state, .search__error-state');
      for (var i = 0; i < existing.length; i++) {
        existing[i].remove();
      }

      var errorDiv = document.createElement('div');
      errorDiv.className = 'search__error-state';
      errorDiv.innerHTML =
        '<p>' + message + '</p>' +
        '<button type="button" onclick="window.location.reload()" class="button button--primary" style="margin-top: 16px;">' +
        '  새로고침' +
        '</button>';
      grid.appendChild(errorDiv);

      grid.classList.add('loaded');
      grid.classList.remove('loading');
    }

    /**
     * Scroll to top of search results
     */
    scrollToTop() {
      var header = document.querySelector('.search-results-header');
      if (header) {
        var offset = 100;
        var top = header.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: top, behavior: 'smooth' });
        return;
      }

      var grid = this.productGrid;
      if (grid) {
        var offset2 = 100;
        var top2 = grid.getBoundingClientRect().top + window.pageYOffset - offset2;
        window.scrollTo({ top: top2, behavior: 'smooth' });
      }
    }

    /**
     * Dispatch collection:reloaded event
     */
    dispatchCollectionReloaded() {
      document.dispatchEvent(new CustomEvent('collection:reloaded'));
    }

    // ==========================================
    // Color Filters
    // ==========================================

    updateColorCounts(colorCounts) {
      if (!colorCounts) return;
      var colorList = document.querySelector('[data-color-filter-list]');
      if (colorList && colorList.querySelector('.color-filter__loading')) {
        this.populateColorFilters(colorCounts);
        return;
      }
      var colorItems = document.querySelectorAll('.color-filter__item');
      colorItems.forEach(function(item) {
        var label = item.querySelector('.color-filter__label');
        var checkbox = item.querySelector('.color-filter__checkbox');
        if (!label) return;
        var colorName = label.getAttribute('data-color-name');
        var count = colorCounts[colorName] || 0;
        var isChecked = checkbox?.checked || false;
        if (count === 0 && !isChecked) { item.style.display = 'none'; } else { item.style.display = ''; }
      });
    }

    populateColorFilters(colorCounts) {
      var colorList = document.querySelector('[data-color-filter-list]');
      if (!colorList) return;
      colorList.innerHTML = '';
      var sortedColors = Object.entries(colorCounts).sort(function(a, b) { return b[1] - a[1]; });
      var self = this;
      sortedColors.forEach(function(entry) {
        var colorKey = entry[0], count = entry[1];
        var isChecked = self.state.colors.includes(colorKey);
        if (count === 0 && !isChecked) return;
        var parts = colorKey.includes(':') ? colorKey.split(':') : [colorKey, null];
        var displayName = parts[0], colorHex = parts[1];
        var isLightColor = colorHex ? self.isLightColor(colorHex) : false;
        var li = document.createElement('li');
        li.className = 'color-filter__item';
        li.innerHTML =
          '<label class="color-filter__label' + (isChecked ? ' active' : '') + '" data-color-name="' + colorKey + '">' +
          '  <input class="color-filter__checkbox visually-hidden" type="checkbox" name="color_filter" value="' + colorKey + '"' + (isChecked ? ' checked' : '') + '>' +
          '  <span class="color-filter__swatch' + (isLightColor ? ' color-filter__swatch--light' : '') + '" style="background-color: ' + (colorHex ? '#' + colorHex : '#ccc') + ';">' +
          '    <svg class="color-filter__check" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '      <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
          '    </svg>' +
          '  </span>' +
          '  <span class="color-filter__name filter-content">' + displayName + '</span>' +
          '</label>';
        colorList.appendChild(li);
      });
    }

    isLightColor(hex) {
      var r = parseInt(hex.substr(0, 2), 16);
      var g = parseInt(hex.substr(2, 2), 16);
      var b = parseInt(hex.substr(4, 2), 16);
      return (r * 299 + g * 587 + b * 114) / 1000 > 180;
    }

    // ==========================================
    // Filter Options
    // ==========================================

    updateFilterOptions(filterOptions) {
      // Price range - update slider max from API data
      if (filterOptions.priceRange) {
        this.updatePriceSliderRange(filterOptions.priceRange.min || 0, filterOptions.priceRange.max || 500000);
      }
      var genderContainer = document.querySelector('[data-gender-filter-container]');
      if (genderContainer) {
        if (filterOptions.genders && filterOptions.genders.length > 0) { genderContainer.removeAttribute('hidden'); this.populateGenderFilter(filterOptions.genders); }
        else { genderContainer.setAttribute('hidden', ''); }
      }
      var sizeContainer = document.querySelector('[data-size-filter-container]');
      if (sizeContainer) {
        if (filterOptions.sizes && filterOptions.sizes.length > 0) { sizeContainer.removeAttribute('hidden'); this.populateSizeFilter(filterOptions.sizes); }
        else { sizeContainer.setAttribute('hidden', ''); }
      }
      var sportsContainer = document.querySelector('[data-sports-filter-container]');
      if (sportsContainer) {
        if (filterOptions.sports && filterOptions.sports.length > 0) { sportsContainer.removeAttribute('hidden'); this.populateSportsFilter(filterOptions.sports); }
        else { sportsContainer.setAttribute('hidden', ''); }
      }
      var wireContainer = document.querySelector('[data-wire-filter-container]');
      if (wireContainer) {
        if (filterOptions.wireTypes && filterOptions.wireTypes.length > 0) { wireContainer.removeAttribute('hidden'); this.populateWireFilter(filterOptions.wireTypes); }
        else { wireContainer.setAttribute('hidden', ''); }
      }
      var sustainableContainer = document.querySelector('[data-sustainable-filter-container]');
      if (sustainableContainer) {
        if (filterOptions.sustainable && filterOptions.sustainable.count > 0) { sustainableContainer.removeAttribute('hidden'); }
        else { sustainableContainer.setAttribute('hidden', ''); }
      }
    }

    populateGenderFilter(options) {
      var list = document.querySelector('[data-gender-filter-list]');
      if (!list) return;
      var genderOptions = this.groupGenderFilterOptions(options);
      var self = this;
      list.innerHTML = '';
      genderOptions.forEach(function(opt) {
        var isChecked = self.isGenderFilterChecked(opt.value);
        if (opt.count === 0 && !isChecked) return;
        var displayName = SearchController.GENDER_DISPLAY_NAMES[opt.labelValue || opt.value] || opt.value;
        var li = document.createElement('li');
        li.className = 'filter-checkbox-item';
        li.innerHTML =
          '<label class="filter-checkbox__label' + (isChecked ? ' active' : '') + '">' +
          '  <input type="checkbox" class="gender-filter__checkbox visually-hidden" name="gender_filter" value="' + opt.value + '"' + (isChecked ? ' checked' : '') + '>' +
          '  <span class="filter-checkbox__box">' +
          '    <svg class="filter-checkbox__icon filter-checkbox__icon--unchecked" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2.5" y="2.5" width="17" height="17" rx="3.5" stroke="currentColor"/></svg>' +
          '    <svg class="filter-checkbox__icon filter-checkbox__icon--checked" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="18" height="18" rx="4" fill="currentColor"/><path d="M6 11L9.5 14.5L16 8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '  </span>' +
          '  <span class="filter-checkbox__text">' + displayName + '</span>' +
          '</label>';
        list.appendChild(li);
      });
    }

    getGenderFilterValues(value) {
      return String(value || '').split(',').map(function(gender) { return gender.trim(); }).filter(Boolean);
    }

    isCommonGenderValue(value) {
      return value === 'U' || value === 'X';
    }

    groupGenderFilterOptions(options) {
      var groupedOptions = [];
      var commonOption = null;

      options.forEach((opt) => {
        if (this.isCommonGenderValue(opt.value)) {
          if (!commonOption) {
            commonOption = { value: '', labelValue: opt.value, values: [], count: 0 };
            groupedOptions.push(commonOption);
          }
          if (!commonOption.values.includes(opt.value)) commonOption.values.push(opt.value);
          commonOption.count += opt.count || 0;
          return;
        }
        groupedOptions.push({ value: opt.value, labelValue: opt.value, count: opt.count || 0 });
      });

      if (commonOption) {
        commonOption.value = commonOption.values.join(',');
      }

      return groupedOptions;
    }

    isGenderFilterChecked(value) {
      var values = this.getGenderFilterValues(value);
      return values.some((gender) => this.state.genders.includes(gender));
    }

    populateSizeFilter(options) {
      var list = document.querySelector('[data-size-filter-list]');
      if (!list) return;
      list.innerHTML = '';
      var self = this;
      if (this.state.filterTemplate === '의류') {
        var groupCounts = {};
        var groupOrder = Object.keys(SearchController.SIZE_GROUP_MAP);
        options.forEach(function(opt) {
          for (var group in SearchController.SIZE_GROUP_MAP) {
            if (SearchController.SIZE_GROUP_MAP[group].includes(opt.value)) { groupCounts[group] = (groupCounts[group] || 0) + opt.count; break; }
          }
        });
        groupOrder.forEach(function(group) {
          if (!groupCounts[group] || groupCounts[group] === 0) return;
          var isSelected = self.state.sizes.includes(group);
          var btn = document.createElement('button');
          btn.type = 'button'; btn.className = 'size-chip' + (isSelected ? ' size-chip--selected' : '');
          btn.dataset.size = group; btn.textContent = group;
          list.appendChild(btn);
        });
      } else {
        options.forEach(function(opt) {
          var isSelected = self.state.sizes.includes(opt.value);
          if (opt.count === 0 && !isSelected) return;
          var btn = document.createElement('button');
          btn.type = 'button'; btn.className = 'size-chip' + (isSelected ? ' size-chip--selected' : '');
          btn.dataset.size = opt.value; btn.textContent = opt.value;
          list.appendChild(btn);
        });
      }
    }

    populateSportsFilter(options) {
      var list = document.querySelector('[data-sports-filter-list]');
      if (!list) return;
      var self = this;
      list.innerHTML = '';
      options.forEach(function(opt) {
        var isChecked = self.state.sports.includes(opt.value);
        if (opt.count === 0 && !isChecked) return;
        var li = document.createElement('li');
        li.className = 'filter-checkbox-item';
        li.innerHTML =
          '<label class="filter-checkbox__label' + (isChecked ? ' active' : '') + '">' +
          '  <input type="checkbox" class="sports-filter__checkbox visually-hidden" name="sports_filter" value="' + opt.value + '"' + (isChecked ? ' checked' : '') + '>' +
          '  <span class="filter-checkbox__box">' +
          '    <svg class="filter-checkbox__icon filter-checkbox__icon--unchecked" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2.5" y="2.5" width="17" height="17" rx="3.5" stroke="currentColor"/></svg>' +
          '    <svg class="filter-checkbox__icon filter-checkbox__icon--checked" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="18" height="18" rx="4" fill="currentColor"/><path d="M6 11L9.5 14.5L16 8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '  </span>' +
          '  <span class="filter-checkbox__text">' + opt.value + '</span>' +
          '</label>';
        list.appendChild(li);
      });
    }

    populateWireFilter(options) {
      var list = document.querySelector('[data-wire-filter-list]');
      if (!list) return;
      var self = this;
      list.innerHTML = '';
      options.forEach(function(opt) {
        var isChecked = self.state.wireTypes.includes(opt.value);
        if (opt.count === 0 && !isChecked) return;
        var li = document.createElement('li');
        li.className = 'filter-checkbox-item';
        li.innerHTML =
          '<label class="filter-checkbox__label' + (isChecked ? ' active' : '') + '">' +
          '  <input type="checkbox" class="wire-filter__checkbox visually-hidden" name="wire_filter" value="' + opt.value + '"' + (isChecked ? ' checked' : '') + '>' +
          '  <span class="filter-checkbox__box">' +
          '    <svg class="filter-checkbox__icon filter-checkbox__icon--unchecked" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2.5" y="2.5" width="17" height="17" rx="3.5" stroke="currentColor"/></svg>' +
          '    <svg class="filter-checkbox__icon filter-checkbox__icon--checked" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="18" height="18" rx="4" fill="currentColor"/><path d="M6 11L9.5 14.5L16 8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '  </span>' +
          '  <span class="filter-checkbox__text">' + opt.value + '</span>' +
          '</label>';
        list.appendChild(li);
      });
    }

    expandSizeGroups(sizeGroups) {
      var expanded = [];
      sizeGroups.forEach(function(group) {
        var members = SearchController.SIZE_GROUP_MAP[group];
        if (members) { expanded.push.apply(expanded, members); } else { expanded.push(group); }
      });
      return expanded;
    }

    // ==========================================
    // Filter Handlers
    // ==========================================

    handleColorFilterChange(checkbox) {
      var colorName = checkbox.value;
      var label = checkbox.closest('.color-filter__label');
      if (checkbox.checked) {
        if (!this.state.colors.includes(colorName)) this.state.colors.push(colorName);
        if (label) label.classList.add('active');
      } else {
        this.state.colors = this.state.colors.filter(function(c) { return c !== colorName; });
        if (label) label.classList.remove('active');
      }
      this.state.page = 1; this.updateURL(); this.renderActiveFacets(); this.debouncedFetchAndRender();
    }

    handleGenderFilterChange(checkbox) {
      var genders = this.getGenderFilterValues(checkbox.value);
      var label = checkbox.closest('.filter-checkbox__label');
      if (checkbox.checked) {
        genders.forEach((gender) => {
          if (!this.state.genders.includes(gender)) this.state.genders.push(gender);
        });
        if (label) label.classList.add('active');
      } else {
        this.state.genders = this.state.genders.filter(function(g) { return !genders.includes(g); });
        if (label) label.classList.remove('active');
      }
      this.state.page = 1; this.updateURL(); this.renderActiveFacets(); this.debouncedFetchAndRender();
    }

    handleSizeFilterChange(chip) {
      var size = chip.dataset.size;
      if (chip.classList.contains('size-chip--selected')) {
        this.state.sizes = this.state.sizes.filter(function(s) { return s !== size; });
        chip.classList.remove('size-chip--selected');
      } else {
        if (!this.state.sizes.includes(size)) this.state.sizes.push(size);
        chip.classList.add('size-chip--selected');
      }
      this.state.page = 1; this.updateURL(); this.renderActiveFacets(); this.debouncedFetchAndRender();
    }

    handleSportsFilterChange(checkbox) {
      var sport = checkbox.value;
      var label = checkbox.closest('.filter-checkbox__label');
      if (checkbox.checked) {
        if (!this.state.sports.includes(sport)) this.state.sports.push(sport);
        if (label) label.classList.add('active');
      } else {
        this.state.sports = this.state.sports.filter(function(s) { return s !== sport; });
        if (label) label.classList.remove('active');
      }
      this.state.page = 1; this.updateURL(); this.renderActiveFacets(); this.debouncedFetchAndRender();
    }

    handleWireFilterChange(checkbox) {
      var wireType = checkbox.value;
      var label = checkbox.closest('.filter-checkbox__label');
      if (checkbox.checked) {
        if (!this.state.wireTypes.includes(wireType)) this.state.wireTypes.push(wireType);
        if (label) label.classList.add('active');
      } else {
        this.state.wireTypes = this.state.wireTypes.filter(function(w) { return w !== wireType; });
        if (label) label.classList.remove('active');
      }
      this.state.page = 1; this.updateURL(); this.renderActiveFacets(); this.debouncedFetchAndRender();
    }

    handleSustainableFilterChange(checkbox) {
      var label = checkbox.closest('.filter-checkbox__label');
      this.state.sustainable = checkbox.checked;
      if (checkbox.checked) { if (label) label.classList.add('active'); }
      else { if (label) label.classList.remove('active'); }
      this.state.page = 1; this.updateURL(); this.renderActiveFacets(); this.debouncedFetchAndRender();
    }

    // ==========================================
    // Price Slider
    // ==========================================

    /**
     * Update price slider min/max from API filterOptions.priceRange
     */
    updatePriceSliderRange(apiMin, apiMax) {
      var slider = document.querySelector('[data-price-slider]');
      if (!slider) return;
      var minInput = slider.querySelector('[data-price-min-input]');
      var maxInput = slider.querySelector('[data-price-max-input]');
      if (!minInput || !maxInput) return;

      var sliderMax = Math.ceil(apiMax / 10000) * 10000;
      if (sliderMax < 10000) sliderMax = 10000;

      minInput.min = 0;
      minInput.max = sliderMax;
      maxInput.min = 0;
      maxInput.max = sliderMax;

      if (this.state.fromPrice === null) minInput.value = 0;
      if (this.state.toPrice === null) maxInput.value = sliderMax;

      this.updatePriceSliderUI();
    }

    setupPriceSlider() {
      var self = this;
      var slider = document.querySelector('[data-price-slider]');
      if (!slider) return;
      var minInput = slider.querySelector('[data-price-min-input]');
      var maxInput = slider.querySelector('[data-price-max-input]');
      var rangeBar = slider.querySelector('[data-price-range-bar]');
      var minDisplay = slider.querySelector('[data-price-min-display]');
      var maxDisplay = slider.querySelector('[data-price-max-display]');
      if (!minInput || !maxInput) return;

      if (this.state.fromPrice !== null) minInput.value = this.state.fromPrice;
      if (this.state.toPrice !== null) maxInput.value = this.state.toPrice;

      var updateSliderVisual = function() {
        var min = parseInt(minInput.value), max = parseInt(maxInput.value), sliderMax = parseInt(maxInput.max);
        if (rangeBar) { rangeBar.style.left = (min / sliderMax) * 100 + '%'; rangeBar.style.width = ((max - min) / sliderMax) * 100 + '%'; }
        if (minDisplay) minDisplay.textContent = min.toLocaleString() + '원';
        if (maxDisplay) maxDisplay.textContent = max.toLocaleString() + '원';
      };

      var debounceTimer;
      var handleSliderChange = function() {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async function() {
          if (self.isLoading) return;
          var min = parseInt(minInput.value), max = parseInt(maxInput.value), sliderMax = parseInt(maxInput.max);
          self.state.fromPrice = min > 0 ? min : null;
          self.state.toPrice = max < sliderMax ? max : null;
          self.state.page = 1;
          self.updateURL();
          self.renderActiveFacets();
          await self.fetchAndRender();
        }, 500);
      };

      minInput.addEventListener('input', function() { if (parseInt(minInput.value) > parseInt(maxInput.value)) minInput.value = maxInput.value; updateSliderVisual(); });
      maxInput.addEventListener('input', function() { if (parseInt(maxInput.value) < parseInt(minInput.value)) maxInput.value = minInput.value; updateSliderVisual(); });
      minInput.addEventListener('change', handleSliderChange);
      maxInput.addEventListener('change', handleSliderChange);
      updateSliderVisual();
    }

    updatePriceSliderUI() {
      var slider = document.querySelector('[data-price-slider]');
      if (!slider) return;
      var minInput = slider.querySelector('[data-price-min-input]');
      var maxInput = slider.querySelector('[data-price-max-input]');
      var rangeBar = slider.querySelector('[data-price-range-bar]');
      var minDisplay = slider.querySelector('[data-price-min-display]');
      var maxDisplay = slider.querySelector('[data-price-max-display]');
      if (!minInput || !maxInput) return;
      var sliderMax = parseInt(maxInput.max);
      var min = this.state.fromPrice !== null ? this.state.fromPrice : 0;
      var max = this.state.toPrice !== null ? this.state.toPrice : sliderMax;
      minInput.value = min; maxInput.value = max;
      if (rangeBar) { rangeBar.style.left = (min / sliderMax) * 100 + '%'; rangeBar.style.width = ((max - min) / sliderMax) * 100 + '%'; }
      if (minDisplay) minDisplay.textContent = min.toLocaleString() + '원';
      if (maxDisplay) maxDisplay.textContent = max.toLocaleString() + '원';
    }

    restorePriceFilterUI() {
      this.updatePriceSliderUI();
    }

    restoreSizeFilterUI() {
      var self = this;
      document.querySelectorAll('.size-chip').forEach(function(chip) { chip.classList.remove('size-chip--selected'); });
      this.state.sizes.forEach(function(size) {
        var chip = document.querySelector('.size-chip[data-size="' + size + '"]');
        if (chip) chip.classList.add('size-chip--selected');
      });
    }

    // ==========================================
    // Active Facets
    // ==========================================

    renderActiveFacets() {
      var badges = [];
      var self = this;

      this.state.colors.forEach(function(color) {
        var colorName = color.split(':')[0];
        badges.push(self.createFacetBadge('color', color, colorName));
      });
      this.getSelectedGenderBadges().forEach(function(genderBadge) {
        badges.push(self.createFacetBadge('gender', genderBadge.value, genderBadge.label));
      });
      this.state.sizes.forEach(function(size) { badges.push(self.createFacetBadge('size', size, size)); });
      this.state.sports.forEach(function(sport) { badges.push(self.createFacetBadge('sport', sport, sport)); });
      this.state.wireTypes.forEach(function(wire) { badges.push(self.createFacetBadge('wire', wire, wire)); });
      if (this.state.sustainable) badges.push(this.createFacetBadge('sustainable', 'true', 'Reduce'));
      if (this.state.fromPrice !== null || this.state.toPrice !== null) {
        var priceLabel = this.getPriceRangeLabel(this.state.fromPrice, this.state.toPrice);
        var priceValue = (this.state.fromPrice || 0) + '-' + (this.state.toPrice || '');
        badges.push(this.createFacetBadge('price', priceValue, priceLabel));
      }

      var hasActiveFilters = badges.length > 0;
      var badgesHTML = badges.join('');

      var plpContainer = document.querySelector('[data-custom-active-facets]');
      if (plpContainer) plpContainer.innerHTML = badgesHTML;
      var plpClearBtn = document.querySelector('.filter-clear-all-plp');
      if (plpClearBtn) plpClearBtn.style.display = hasActiveFilters ? 'flex' : 'none';
      var drawerContainer = document.querySelector('[data-custom-active-facets-drawer]');
      if (drawerContainer) drawerContainer.innerHTML = badgesHTML;
      var drawerWrapper = document.querySelector('[data-drawer-active-facets-wrapper]');
      if (drawerWrapper) drawerWrapper.style.display = hasActiveFilters ? '' : 'none';
      var resetBtn = document.getElementById('facets-reset-plp');
      if (resetBtn) resetBtn.style.display = hasActiveFilters ? '' : 'none';
    }

    getPriceRangeLabel(fromPrice, toPrice) {
      if (fromPrice === null && toPrice === 50000) return '50,000원 이하';
      if (fromPrice === 50000 && toPrice === 100000) return '50,000 - 100,000원';
      if (fromPrice === 100000 && toPrice === 150000) return '100,000 - 150,000원';
      if (fromPrice === 150000 && toPrice === 200000) return '150,000 - 200,000원';
      if (fromPrice === 200000 && toPrice === null) return '200,000원 +';
      return (fromPrice ? fromPrice.toLocaleString() : '0') + '원 - ' + (toPrice ? toPrice.toLocaleString() : '') + '원';
    }

    createFacetBadge(type, value, label) {
      return '<button type="button" class="filter-badge" data-facet-type="' + type + '" data-facet-value="' + value + '">' +
        '<span class="filter-badge__label">' + label + '</span>' +
        '<span class="filter-badge__remove"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
        '</button>';
    }

    async handleFacetRemove(type, value) {
      if (this.isLoading) return;
      if (type === 'color') {
        this.state.colors = this.state.colors.filter(function(c) { return c !== value; });
        var cb = document.querySelector('.color-filter__checkbox[value="' + value + '"]');
        if (cb) { cb.checked = false; var l = cb.closest('.color-filter__label'); if (l) l.classList.remove('active'); }
      } else if (type === 'price') {
        this.state.fromPrice = null; this.state.toPrice = null;
        this.updatePriceSliderUI();
      } else if (type === 'gender') {
        var genderValues = this.getGenderFilterValues(value);
        this.state.genders = this.state.genders.filter(function(g) { return !genderValues.includes(g); });
        var gcb = this.findGenderCheckbox(value);
        if (gcb) { gcb.checked = false; var gl = gcb.closest('.filter-checkbox__label'); if (gl) gl.classList.remove('active'); }
      } else if (type === 'size') {
        this.state.sizes = this.state.sizes.filter(function(s) { return s !== value; });
        var chip = document.querySelector('.size-chip[data-size="' + value + '"]');
        if (chip) chip.classList.remove('size-chip--selected');
      } else if (type === 'sport') {
        this.state.sports = this.state.sports.filter(function(s) { return s !== value; });
        var scb = document.querySelector('.sports-filter__checkbox[value="' + value + '"]');
        if (scb) { scb.checked = false; var sl = scb.closest('.filter-checkbox__label'); if (sl) sl.classList.remove('active'); }
      } else if (type === 'wire') {
        this.state.wireTypes = this.state.wireTypes.filter(function(w) { return w !== value; });
        var wcb = document.querySelector('.wire-filter__checkbox[value="' + value + '"]');
        if (wcb) { wcb.checked = false; var wl = wcb.closest('.filter-checkbox__label'); if (wl) wl.classList.remove('active'); }
      } else if (type === 'sustainable') {
        this.state.sustainable = false;
        var sucb = document.querySelector('.sustainable-filter__checkbox');
        if (sucb) { sucb.checked = false; var sul = sucb.closest('.filter-checkbox__label'); if (sul) sul.classList.remove('active'); }
      }
      this.state.page = 1; this.updateURL(); await this.fetchAndRender();
    }

    getSelectedGenderBadges() {
      var badges = [];
      var hasCommonGender = false;

      this.state.genders.forEach((gender) => {
        if (this.isCommonGenderValue(gender)) {
          hasCommonGender = true;
          return;
        }
        badges.push({ value: gender, label: SearchController.GENDER_DISPLAY_NAMES[gender] || gender });
      });

      if (hasCommonGender) {
        badges.push({ value: 'U,X', label: '공용' });
      }

      return badges;
    }

    findGenderCheckbox(value) {
      var targetValues = this.getGenderFilterValues(value);
      var checkboxes = document.querySelectorAll('.gender-filter__checkbox');
      for (var i = 0; i < checkboxes.length; i++) {
        var checkboxValues = this.getGenderFilterValues(checkboxes[i].value);
        if (checkboxes[i].value === value || checkboxValues.some(function(gender) { return targetValues.includes(gender); })) {
          return checkboxes[i];
        }
      }
      return null;
    }

    async clearAllFilters() {
      if (this.isLoading) return;
      this.state.colors = []; this.state.fromPrice = null; this.state.toPrice = null;
      this.state.genders = []; this.state.sizes = []; this.state.sports = [];
      this.state.wireTypes = []; this.state.sustainable = false;
      this.resetFilterUI();
      this.state.page = 1; this.updateURL(); await this.fetchAndRender();
    }

    /**
     * Reset all filter UI elements to unchecked/default state
     */
    resetFilterUI() {
      document.querySelectorAll('.color-filter__checkbox').forEach(function(cb) { cb.checked = false; var l = cb.closest('.color-filter__label'); if (l) l.classList.remove('active'); });
      this.updatePriceSliderUI();
      document.querySelectorAll('.gender-filter__checkbox').forEach(function(cb) { cb.checked = false; var l = cb.closest('.filter-checkbox__label'); if (l) l.classList.remove('active'); });
      document.querySelectorAll('.size-chip').forEach(function(c) { c.classList.remove('size-chip--selected'); });
      document.querySelectorAll('.sports-filter__checkbox').forEach(function(cb) { cb.checked = false; var l = cb.closest('.filter-checkbox__label'); if (l) l.classList.remove('active'); });
      document.querySelectorAll('.wire-filter__checkbox').forEach(function(cb) { cb.checked = false; var l = cb.closest('.filter-checkbox__label'); if (l) l.classList.remove('active'); });
      document.querySelectorAll('.sustainable-filter__checkbox').forEach(function(cb) { cb.checked = false; var l = cb.closest('.filter-checkbox__label'); if (l) l.classList.remove('active'); });
      this.renderActiveFacets();
    }

    // ==========================================
    // Cache
    // ==========================================

    /**
     * Build cache key
     */
    buildCacheKey() {
      var s = this.state;
      var discountType = sessionStorage.getItem('discount_type') || 'normal';
      return 'search_' + discountType + '_' + s.keyword + '_' + s.category + '_' + s.sortBy + '_' + s.page + '_' + s.pageSize +
        '_' + s.colors.join(',') + '_' + s.fromPrice + '_' + s.toPrice +
        '_' + s.genders.join(',') + '_' + s.sizes.join(',') + '_' + s.sports.join(',') +
        '_' + s.wireTypes.join(',') + '_' + s.sustainable;
    }

    /**
     * Get from sessionStorage cache
     */
    getFromCache(key) {
      try {
        var item = sessionStorage.getItem(key);
        if (!item) return null;

        var parsed = JSON.parse(item);
        var data = parsed.data;
        var timestamp = parsed.timestamp;

        if (Date.now() - timestamp < SearchController.CACHE_TTL) {
          return data;
        }

        sessionStorage.removeItem(key);
      } catch (e) {
        console.error('SearchController: Cache read error', e);
      }
      return null;
    }

    /**
     * Save to sessionStorage cache
     * Excludes products[] to avoid sessionStorage quota issues
     */
    saveToCache(key, data) {
      try {
        // Cache metadata only (exclude products[] to save storage)
        var cacheData = {
          success: data.success,
          totalCount: data.totalCount,
          totalPages: data.totalPages,
          page: data.page,
          pageSize: data.pageSize,
          sortBy: data.sortBy,
          session: data.session,
          handles: data.handles,
          colorCounts: data.colorCounts,
          filterOptions: data.filterOptions,
          filterTemplate: data.filterTemplate,
          products: data.products
        };

        sessionStorage.setItem(key, JSON.stringify({
          data: cacheData,
          timestamp: Date.now()
        }));
      } catch (e) {
        // If storage is full, clear old entries and retry
        if (e.name === 'QuotaExceededError') {
          this.clearExpiredCache();
          try {
            // Retry without products to save space
            var lightData = {
              success: data.success,
              totalCount: data.totalCount,
              totalPages: data.totalPages,
              page: data.page,
              pageSize: data.pageSize,
              sortBy: data.sortBy,
              session: data.session,
              handles: data.handles,
              colorCounts: data.colorCounts,
              filterOptions: data.filterOptions,
              filterTemplate: data.filterTemplate
              // products excluded
            };
            sessionStorage.setItem(key, JSON.stringify({
              data: lightData,
              timestamp: Date.now()
            }));
          } catch (e2) {
            console.error('SearchController: Cache write error after cleanup', e2);
          }
        } else {
          console.error('SearchController: Cache write error', e);
        }
      }
    }

    /**
     * Clear expired cache entries
     */
    clearExpiredCache() {
      try {
        var keysToRemove = [];
        for (var i = 0; i < sessionStorage.length; i++) {
          var key = sessionStorage.key(i);
          if (key && key.indexOf('search_') === 0) {
            try {
              var parsed = JSON.parse(sessionStorage.getItem(key));
              if (Date.now() - parsed.timestamp >= SearchController.CACHE_TTL) {
                keysToRemove.push(key);
              }
            } catch (e) {
              keysToRemove.push(key);
            }
          }
        }
        for (var j = 0; j < keysToRemove.length; j++) {
          sessionStorage.removeItem(keysToRemove[j]);
        }
      } catch (e) {
        // Ignore cleanup errors
      }
    }
  }

  // Initialize
  if (typeof window !== 'undefined') {
    window.SearchController = SearchController;
    new SearchController();
  }
})();
